import {
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).map(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    backgroundImage: 'none',
    colors: ['#161616', '#161616'], // Match sidebar background
  };
});

// Konteks Dark Theme - Based on Revamp Design System
// Colors from globals.css .dark class
export const konteksDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: {
        main: '#F0A31C', // Primary gold color
        light: '#f4b24f',
        dark: '#d17f0b',
        contrastText: '#0c0c0c',
      },
      secondary: {
        main: '#252525',
        light: '#404040',
        dark: '#1e1e1e',
        contrastText: '#f5f5f5',
      },
      error: {
        main: '#dc2626',
        light: '#ef4444',
        dark: '#b91c1c',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
      },
      success: {
        main: '#22c55e',
        light: '#4ade80',
        dark: '#16a34a',
      },
      info: {
        main: '#5292F4',
        light: '#60a5fa',
        dark: '#2563eb',
      },
      action: {
        active: '#F0A31C',
        hover: 'rgba(240, 163, 28, 0.1)',
        selected: 'rgba(240, 163, 28, 0.15)',
        disabled: 'rgba(160, 160, 160, 0.3)',
        disabledBackground: 'rgba(30, 30, 30, 0.5)',
      },
      common: {
        white: '#FFFFFF',
        black: '#000000',
      },
      border: '#2a2a2a',
      divider: '#2a2a2a',
      navigation: {
        ...palettes.dark.navigation,
        background: '#161616', // sidebar background
        color: '#f5f5f5', // sidebar foreground
        selectedColor: '#0c0c0c', // primary-foreground
        indicator: '#F0A31C', // sidebar-primary
        submenu: {
          background: '#161616',
        },
      },
      text: {
        primary: '#f5f5f5', // foreground
        secondary: '#a0a0a0', // muted-foreground
        disabled: '#606060',
        hint: '#808080',
      },
      background: {
        default: '#0c0c0c', // background
        paper: '#161616', // card
      },
      grey: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#e5e5e5',
        300: '#d4d4d4',
        400: '#a0a0a0',
        500: '#737373',
        600: '#525252',
        700: '#404040',
        800: '#262626',
        900: '#171717',
      },
    },
  }),
  typography: {
    htmlFontSize: 14, // Base font size from Revamp
    fontFamily:
      '"Geist", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontFamily: '"Geist Mono", "Fira Code", monospace',
      fontSize: '1.5rem', // 24px
      fontWeight: 500,
      marginBottom: 16,
    },
    h2: {
      fontFamily: '"Geist Mono", "Fira Code", monospace',
      fontSize: '1.25rem', // 20px
      fontWeight: 500,
      marginBottom: 14,
    },
    h3: {
      fontFamily: '"Geist Mono", "Fira Code", monospace',
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      marginBottom: 12,
    },
    h4: {
      fontFamily: '"Geist", "Inter", system-ui, sans-serif',
      fontSize: '1rem', // 16px
      fontWeight: 500,
      marginBottom: 10,
    },
    h5: {
      fontFamily: '"Geist", "Inter", system-ui, sans-serif',
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      marginBottom: 8,
    },
    h6: {
      fontFamily: '"Geist", "Inter", system-ui, sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      marginBottom: 8,
    },
  },
  shape: {
    borderRadius: 6,
  },
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  components: {
    // Global CSS baseline
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Geist';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/GeistVF.woff2') format('woff2');
          font-weight: 100 900;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Geist Mono';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/GeistMonoVF.woff2') format('woff2');
          font-weight: 100 900;
          font-style: normal;
          font-display: swap;
        }
        html {
          background-color: #0c0c0c;
          font-size: 14px;
        }
        body {
          background-color: #0c0c0c !important;
          font-feature-settings: "cv02", "cv03", "cv04", "cv11";
          margin: 0;
          padding: 0;
        }
        * {
          box-sizing: border-box;
        }
        #root {
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          background-color: #0c0c0c;
        }
        /* Fix sidebar submenu hover gap */
        [data-testid="item-with-submenu"] {
          padding-right: 4px;
        }
      `,
    },

    // Fix main content width / horizontal scroll
    BackstageContent: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          padding: '24px',
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          width: '100%',
          overflowX: 'hidden',
        },
      },
    },

    // Header with sidebar-matching background
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          background: '#161616', // Same as sidebar background
          paddingBottom: '12px',
          paddingTop: '12px',
          paddingLeft: '24px',
          paddingRight: '44px',
          minHeight: '70px',
          height: '70px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'none',
          margin: 0,
          borderBottom: '1px solid #2a2a2a',
        }),
        title: () => ({
          color: '#f5f5f5',
          fontFamily: '"Geist", "Inter", system-ui, sans-serif',
          fontWeight: 600,
          fontSize: '1.25rem',
          lineHeight: 1.25,
        }),
        subtitle: () => ({
          color: '#a0a0a0',
          fontFamily: '"Geist", "Inter", system-ui, sans-serif',
          fontSize: '0.875rem',
          lineHeight: 1.25,
          marginTop: '2px',
        }),
      },
    },

    BackstageHeaderLabel: {
      styleOverrides: {
        label: () => ({
          color: '#a0a0a0',
        }),
        value: () => ({
          color: '#f5f5f5',
        }),
      },
    },

    BackstageSelectInputBase: {
      styleOverrides: {
        input: () => ({
          borderColor: '#2a2a2a',
          backgroundColor: '#1e1e1e',
          borderRadius: '6px',
        }),
      },
    },

    // Table Overrides
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          paddingTop: '16px',
          backgroundColor: '#161616',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: () => ({
          '&:hover': {
            backgroundColor: '#1e1e1e',
          },
          borderTopColor: '#2a2a2a',
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: () => ({
          borderBottomColor: '#2a2a2a',
          padding: '12px 16px !important',
        }),
        body: () => ({
          padding: '12px 16px !important',
        }),
        head: () => ({
          fontWeight: 600,
          backgroundColor: '#161616',
          color: '#a0a0a0',
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          letterSpacing: '0.05em',
        }),
      },
    },
    BackstageOverflowTooltip: {
      styleOverrides: {
        // @ts-expect-error typo not listed
        typo: {
          lineHeight: '1.5',
        },
      },
    },

    // Paper / Card Overrides
    MuiPaper: {
      styleOverrides: {
        root: () => ({
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: '#2a2a2a',
          color: '#f5f5f5',
          backgroundColor: '#161616',
        }),
        rounded: () => ({
          borderRadius: '8px',
          overflow: 'hidden',
        }),
        elevation0: () => ({
          boxShadow: 'none',
        }),
        elevation1: () => ({
          boxShadow: 'none',
        }),
        elevation2: () => ({
          boxShadow: 'none',
        }),
      },
    },

    // Card Overrides
    MuiCard: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#161616',
          borderColor: '#2a2a2a',
          borderRadius: '8px',
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: () => ({
          borderBottom: '1px solid #1a1a20',
          padding: '10px 20px',
        }),
        title: () => ({
          fontFamily: '"Geist", "Inter", system-ui, sans-serif',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: () => ({
          padding: '20px',
        }),
      },
    },

    // Button Overrides
    MuiButton: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          fontSize: '1rem',
        }),
        containedPrimary: () => ({
          backgroundColor: '#F0A31C',
          color: '#0c0c0c',
          '&:hover': {
            backgroundColor: '#d17f0b',
            color: '#0c0c0c',
          },
        }),
        outlinedPrimary: () => ({
          borderColor: '#F0A31C',
          color: '#F0A31C',
          '&:hover': {
            borderColor: '#f4b24f',
            color: '#f4b24f',
            backgroundColor: 'rgba(240, 163, 28, 0.1)',
          },
        }),
        textPrimary: () => ({
          color: '#F0A31C',
          '&:hover': {
            backgroundColor: 'rgba(240, 163, 28, 0.1)',
          },
        }),
      },
    },

    // Input / TextField Overrides
    MuiTextField: {
      styleOverrides: {
        root: () => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            backgroundColor: '#1e1e1e',
            '& fieldset': {
              borderColor: '#2a2a2a',
            },
            '&:hover fieldset': {
              borderColor: '#404040',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#F0A31C',
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          backgroundColor: '#1e1e1e',
          minHeight: '40px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2a2a2a',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#404040',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#F0A31C',
          },
        }),
        input: () => ({
          padding: '12px 16px',
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: () => ({
          fontSize: '1rem',
          lineHeight: 1.5,
          // Add left padding when start adornment is present (underline variant)
          '&.MuiInputBase-adornedStart': {
            paddingLeft: '8px',
          },
        }),
      },
    },
    // Input adornment (icons) styling
    MuiInputAdornment: {
      styleOverrides: {
        root: () => ({
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem', // 20px - smaller icon size
            color: '#a0a0a0',
          },
        }),
        positionStart: () => ({
          paddingLeft: '8px',
        }),
      },
    },
    // Force standard (underline) inputs to use outlined styling
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          backgroundColor: '#1e1e1e',
          border: '1px solid #2a2a2a',
          padding: '0',
          paddingLeft: '0',
          '&:hover': {
            borderColor: '#404040',
          },
          '&.Mui-focused': {
            borderColor: '#F0A31C',
          },
          '&.Mui-error': {
            borderColor: '#dc2626',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(30, 30, 30, 0.5)',
          },
          '&::before, &::after': {
            display: 'none',
          },
          // Add left padding when start adornment (left icon) is present
          '& .MuiInputAdornment-positionStart': {
            marginLeft: '12px',
            marginRight: '0',
          },
        }),
        input: () => ({
          padding: '12px 16px',
          '&.MuiInput-inputAdornedStart': {
            paddingLeft: '8px',
          },
        }),
      },
    },
    // Force filled inputs to use outlined styling
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          backgroundColor: '#1e1e1e',
          border: '1px solid #2a2a2a',
          paddingLeft: '0',
          '&:hover': {
            backgroundColor: '#1e1e1e',
            borderColor: '#404040',
          },
          '&.Mui-focused': {
            backgroundColor: '#1e1e1e',
            borderColor: '#F0A31C',
          },
          '&.Mui-error': {
            borderColor: '#dc2626',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(30, 30, 30, 0.5)',
          },
          '&::before, &::after': {
            display: 'none',
          },
          // Add left padding when start adornment (left icon) is present
          '& .MuiInputAdornment-positionStart': {
            marginLeft: '12px',
            marginRight: '0',
          },
        }),
        input: () => ({
          padding: '12px 16px',
          '&.MuiFilledInput-inputAdornedStart': {
            paddingLeft: '8px',
          },
        }),
      },
    },

    // Select Overrides
    MuiSelect: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
        }),
      },
    },

    // Chip Overrides
    MuiChip: {
      styleOverrides: {
        root: () => ({
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          fontWeight: 500,
          fontSize: '0.75rem',
          margin: '2px',
        }),
        sizeSmall: {
          height: '20px',
          minWidth: '20px',
        },
        label: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          lineHeight: 1,
        },
        labelSmall: {
          padding: '0 6px',
        },
        colorPrimary: () => ({
          backgroundColor: 'rgba(240, 163, 28, 0.2)',
          color: '#F0A31C',
          borderColor: 'rgba(240, 163, 28, 0.2)',
        }),
        colorSuccess: () => ({
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          color: '#22c55e',
        }),
        colorError: () => ({
          backgroundColor: 'rgba(220, 38, 38, 0.2)',
          color: '#dc2626',
        }),
        colorWarning: () => ({
          backgroundColor: 'rgba(245, 158, 11, 0.2)',
          color: '#f59e0b',
        }),
      },
    },

    // Badge Overrides
    MuiBadge: {
      styleOverrides: {
        badge: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '20px',
          height: '20px',
          padding: '0 4px',
          fontSize: '0.75rem',
          lineHeight: 1,
          fontWeight: 600,
        },
      },
    },

    // Sidebar Overrides
    BackstageSidebar: {
      styleOverrides: {
        drawer: () => ({
          backgroundColor: '#161616',
          borderRight: 'solid 1px #2a2a2a',
          alignItems: 'center',
          padding: '0 4px',
          top: '52px !important',
          height: 'calc(100vh - 52px) !important',
          '& > :first-child': {
            alignSelf: 'flex-start',
          },
        }),
      },
    },
    BackstageSidebarDivider: {
      styleOverrides: {
        root: () => ({
          width: '100%',
          height: '1px',
          backgroundColor: '#2a2a2a',
          border: 'none',
        }),
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px !important',
          margin: '2px 4px !important',
          fontSize: '0.875rem',
          height: '40px !important',
          width: 'calc(100% - 8px) !important',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
        }),
        iconContainer: () => ({
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '0',
        }),
        submenuArrow: () => ({
          display: 'flex',
          alignItems: 'center',
          marginLeft: 'auto',
        }),
        selected: () => ({
          backgroundColor: 'rgba(240, 163, 28, 0.1) !important',
          borderRadius: '6px !important',
          borderLeft: 'none !important',
          color: '#F0A31C !important',
          margin: '2px 4px !important',
          width: 'calc(100% - 8px) !important',
          position: 'relative',
          overflow: 'visible',
          fontWeight: '600 !important',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '0px',
            top: '20%',
            height: '60%',
            width: '2px',
            backgroundColor: '#F0A31C',
            borderRadius: '2px',
          },
          '&:hover': {
            backgroundColor: 'rgba(240, 163, 28, 0.15) !important',
            color: '#F0A31C !important',
          },
        }),
        open: () => ({
          marginTop: '2px',
          marginBottom: '2px',
          width: 'calc(100% - 8px) !important',
        }),
        closed: () => ({
          width: 'auto !important',
          margin: '2px 4px !important',
        }),
        highlightable: () => ({
          '&:hover': {
            backgroundColor: '#1e1e1e !important',
            borderRadius: '6px !important',
            color: '#f5f5f5 !important',
          },
        }),
        highlighted: () => ({
          backgroundColor: '#1e1e1e !important',
        }),
        label: () => ({
          fontWeight: 400,
          fontSize: '0.875rem',
        }),
        buttonItem: () => ({
          margin: '0 !important',
          padding: '0 !important',
        }),
        secondaryAction: () => ({
          marginRight: '0 !important',
        }),
      },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        selected: () => ({
          backgroundColor: 'rgba(240, 163, 28, 0.1)',
          borderRadius: '6px',
          color: '#F0A31C',
          '&:hover': {
            backgroundColor: 'rgba(240, 163, 28, 0.15)',
            color: '#F0A31C',
          },
        }),
        item: () => ({
          marginTop: 0,
          marginBottom: 0,
          paddingTop: '8px',
          paddingBottom: '8px',
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: '#1e1e1e',
            color: '#f5f5f5',
          },
        }),
      },
    },
    BackstageSidebarSubmenu: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '8px',
          '& > div': {
            gap: 0,
            padding: 0,
          },
        },
      },
    },

    // Tabs Overrides
    MuiTabs: {
      styleOverrides: {
        root: () => ({
          minHeight: '42px !important',
          backgroundColor: '#161616',
          marginLeft: '0 !important',
        }),
        indicator: () => ({
          backgroundColor: '#F0A31C',
          height: '2px',
          borderRadius: '2px',
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: () => ({
          textTransform: 'none',
          fontWeight: '400 !important',
          fontSize: '0.8125rem !important',
          minHeight: '42px !important',
          padding: '6px 12px !important',
          color: '#a0a0a0',
          '&.Mui-selected': {
            color: '#F0A31C',
            fontWeight: '500 !important',
          },
        }),
      },
    },

    // Tooltip Overrides
    MuiTooltip: {
      styleOverrides: {
        tooltip: () => ({
          backgroundColor: '#252525',
          color: '#f5f5f5',
          fontSize: '0.75rem',
          borderRadius: '4px',
          padding: '6px 12px',
          border: '1px solid #2a2a2a',
        }),
      },
    },

    // Dialog Overrides
    MuiDialog: {
      styleOverrides: {
        paper: () => ({
          backgroundColor: '#161616',
          borderRadius: '8px',
          border: '1px solid #2a2a2a',
        }),
      },
    },

    // Menu Overrides
    MuiMenu: {
      styleOverrides: {
        paper: () => ({
          backgroundColor: '#161616',
          borderRadius: '6px',
          border: '1px solid #2a2a2a',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: () => ({
          fontSize: '0.875rem',
          padding: '8px 16px',
          '&:hover': {
            backgroundColor: '#1e1e1e',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(240, 163, 28, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(240, 163, 28, 0.15)',
            },
          },
        }),
      },
    },

    // Link Overrides
    MuiLink: {
      styleOverrides: {
        root: () => ({
          color: '#F0A31C',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            color: '#f4b24f',
          },
        }),
      },
    },

    // Breadcrumb Overrides
    MuiBreadcrumbs: {
      styleOverrides: {
        root: () => ({
          fontSize: '0.875rem',
        }),
        separator: () => ({
          color: '#a0a0a0',
        }),
      },
    },

    // Avatar Overrides
    MuiAvatar: {
      styleOverrides: {
        root: () => ({
          backgroundColor: 'rgba(240, 163, 28, 0.1)',
          color: '#F0A31C',
        }),
      },
    },

    // Divider Overrides
    MuiDivider: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#2a2a2a',
        }),
      },
    },

    // Skeleton Overrides
    MuiSkeleton: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#1e1e1e',
        }),
      },
    },

    // Alert Overrides
    MuiAlert: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
        }),
        standardSuccess: () => ({
          backgroundColor: '#0d2818', // solid dark green
          color: '#22c55e',
          border: '1px solid #166534',
        }),
        standardError: () => ({
          backgroundColor: '#2a1215', // solid dark red
          color: '#dc2626',
          border: '1px solid #991b1b',
        }),
        standardWarning: () => ({
          backgroundColor: '#2a1f0a', // solid dark amber
          color: '#f59e0b',
          border: '1px solid #b45309',
        }),
        standardInfo: () => ({
          backgroundColor: '#0f1a2e', // solid dark blue
          color: '#5292F4',
          border: '1px solid #1e40af',
        }),
      },
    },

    // Switch Overrides
    MuiSwitch: {
      styleOverrides: {
        root: () => ({
          width: 44,
          height: 24,
          padding: 0,
        }),
        switchBase: () => ({
          padding: 2,
          '&.Mui-checked': {
            color: '#0c0c0c',
            '& + .MuiSwitch-track': {
              backgroundColor: '#F0A31C',
              opacity: 1,
            },
          },
        }),
        thumb: () => ({
          width: 20,
          height: 20,
        }),
        track: () => ({
          borderRadius: 12,
          backgroundColor: '#404040',
          opacity: 1,
        }),
      },
    },

    // Progress Overrides
    MuiLinearProgress: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#1e1e1e',
          borderRadius: '4px',
          height: '6px',
        }),
        bar: () => ({
          backgroundColor: '#F0A31C',
          borderRadius: '4px',
        }),
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: () => ({
          color: '#F0A31C',
        }),
      },
    },
  },
});
